// src/context/CrmThemeContext.js — Light/dark theme for the internal CRM.
// Mirrors PortalThemeContext but with its own storage key, so the two sides
// keep separate preferences. Light mode is preview-only for now (see
// lib/preview.js); everyone else is pinned to 'dark'.
import { createContext, useContext, useEffect, useState } from 'react'
import { useIsPreview } from '../lib/preview'

const THEME_KEY = 'rga_crm_theme'

const CrmThemeContext = createContext({ theme: 'dark', toggleTheme: () => {}, canToggle: false })

export function CrmThemeProvider({ children }) {
  const canToggle = useIsPreview()
  const [stored, setStored] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark' }
    catch { return 'dark' }
  })
  const theme = canToggle ? stored : 'dark'

  // On <html> rather than the layout root so portaled popovers (EntityPicker)
  // pick it up too. Cleared on unmount so the partner portal never inherits it.
  useEffect(() => {
    const el = document.documentElement
    if (theme === 'light') el.setAttribute('data-crm-theme', 'light')
    else el.removeAttribute('data-crm-theme')
    return () => el.removeAttribute('data-crm-theme')
  }, [theme])

  function toggleTheme() {
    setStored(t => {
      const next = t === 'light' ? 'dark' : 'light'
      try { localStorage.setItem(THEME_KEY, next) } catch {}
      return next
    })
  }

  return (
    <CrmThemeContext.Provider value={{ theme, toggleTheme, canToggle }}>
      {children}
    </CrmThemeContext.Provider>
  )
}

export function useCrmTheme() {
  return useContext(CrmThemeContext)
}
